import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { TipoAnagrafica } from '../api/anagrafiche.js';
import { ErroreApi } from '../api/cliente.js';
import { useAnagraficheSimili, useCreaAnagrafica } from '../api/creazione.js';
import { useLimiti } from '../api/scheda.js';
import { Modale } from '../componenti/Modale.js';
import { t } from '../i18n/lingua.js';

/**
 * Un'anagrafica nuova.
 *
 * Nasce con poco: ragione sociale e, se ci sono, i codici di Business
 * Central. Indirizzi, contatti e recapiti si aggiungono subito dopo dalla
 * scheda — dove ci sono già i loro moduli, con i controlli sui limiti di BC —
 * e infatti appena creata la scheda si apre da sola.
 *
 * Mentre si scrive il nome, se qualcuno si chiama già così lo si dice. Non
 * blocca: due anagrafiche con lo stesso nome e sedi diverse esistono davvero,
 * e decidere al posto di chi lavora sarebbe peggio che avvisarlo.
 */
export function ModaleNuovaAnagrafica({
  tipo,
  titolo,
  onChiudi,
}: {
  tipo: TipoAnagrafica;
  /** Come si chiama questo tipo al singolare: «Cliente», «Fornitore». */
  titolo: string;
  onChiudi: () => void;
}) {
  const navigate = useNavigate();
  const crea = useCreaAnagrafica();
  const limiti = useLimiti();

  const [campi, setCampi] = useState({
    name: '',
    bcCodeSpa: '',
    bcCodeInc: '',
    taxNumber: '',
    taxCardId: '',
    notes: '',
  });

  const cambia = (chiave: keyof typeof campi, valore: string) =>
    setCampi((c) => ({ ...c, [chiave]: valore }));

  const simili = useAnagraficheSimili(campi.name);
  const errore = crea.error instanceof ErroreApi ? crea.error : null;
  const completo = campi.name.trim().length > 0;

  const conferma = () =>
    crea.mutate(
      {
        type: tipo,
        name: campi.name.trim(),
        bcCodeSpa: campi.bcCodeSpa.trim() || null,
        bcCodeInc: campi.bcCodeInc.trim() || null,
        taxNumber: campi.taxNumber.trim() || null,
        taxCardId: campi.taxCardId.trim() || null,
        notes: campi.notes.trim() || null,
      },
      {
        onSuccess: ({ id }) => {
          onChiudi();
          // si entra nella scheda: il passo dopo è quasi sempre l'indirizzo
          void navigate({ to: `/${percorsoDi(tipo)}/$id`, params: { id: String(id) } });
        },
      },
    );

  const massimo = (campo: 'name' | 'bcCode' | 'taxNumber' | 'taxCardId') =>
    limiti.data?.limits?.[campo];

  return (
    <Modale
      titolo={`${t('Nuovo')} ${titolo.toLowerCase()}`}
      nota={t(
        'Indirizzi, contatti e recapiti si aggiungono dopo, dalla scheda: appena creata si apre da sola.',
      )}
      larga
      inCorso={crea.isPending}
      modificata={Object.values(campi).some((v) => v.trim() !== '')}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn" onClick={onChiudi} disabled={crea.isPending}>
            {t('Annulla')}
          </button>
          <button
            className="btn btn-primary"
            onClick={conferma}
            disabled={!completo || crea.isPending}
          >
            {crea.isPending ? t('Creo…') : t('Crea')}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      <div className="fields">
        <div className="field span-2">
          <label htmlFor="na-nome">
            {t('Ragione sociale')}
            <span className="req">*</span>
          </label>
          <input
            id="na-nome"
            className="input"
            value={campi.name}
            maxLength={massimo('name')}
            autoFocus
            onChange={(e) => cambia('name', e.target.value)}
          />
          {/* chi si chiama già così: si vede prima di creare il doppione */}
          {(simili.data?.rows ?? []).length > 0 && (
            <div className="avviso" role="status" style={{ marginTop: 8 }}>
              <span className="avviso-segno">▲</span>
              <span>
                {t('Esiste già:')}{' '}
                {simili.data!.rows.map((r) => r.name + (r.bcCodeSpa ? ` (${r.bcCodeSpa})` : '')).join(', ')}
                {'. '}
                {t('Puoi crearne un’altra lo stesso, se è una sede diversa.')}
              </span>
            </div>
          )}
        </div>

        <div className="field">
          <label htmlFor="na-bc-spa">{t('Codice BC — SPA')}</label>
          <input
            id="na-bc-spa"
            className="input num"
            value={campi.bcCodeSpa}
            maxLength={massimo('bcCode')}
            onChange={(e) => cambia('bcCodeSpa', e.target.value)}
          />
          <div className="hint">{t('Lascialo vuoto se in BC non c’è ancora.')}</div>
        </div>

        <div className="field">
          <label htmlFor="na-bc-inc">{t('Codice BC — Inc')}</label>
          <input
            id="na-bc-inc"
            className="input num"
            value={campi.bcCodeInc}
            maxLength={massimo('bcCode')}
            onChange={(e) => cambia('bcCodeInc', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="na-piva">{t('Partita IVA')}</label>
          <input
            id="na-piva"
            className="input num"
            value={campi.taxNumber}
            maxLength={massimo('taxNumber')}
            onChange={(e) => cambia('taxNumber', e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="na-cf">{t('Codice fiscale')}</label>
          <input
            id="na-cf"
            className="input num"
            value={campi.taxCardId}
            maxLength={massimo('taxCardId')}
            onChange={(e) => cambia('taxCardId', e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label htmlFor="na-note">{t('Annotazioni')}</label>
          <textarea
            id="na-note"
            className="textarea"
            rows={3}
            value={campi.notes}
            onChange={(e) => cambia('notes', e.target.value)}
          />
        </div>
      </div>
    </Modale>
  );
}

/** Il pezzo di indirizzo su cui vive questo tipo di anagrafica. */
function percorsoDi(tipo: TipoAnagrafica): string {
  const percorsi: Record<string, string> = {
    customer: 'customers',
    supplier: 'suppliers',
    agent: 'agents',
    lead: 'leads',
    prospect: 'prospects',
    operator: 'operators',
  };
  return percorsi[tipo] ?? 'customers';
}
